import type React from 'react';

import { Button } from '@change/ds-core-ui/Button';
import { ArrowRightIcon } from '@change/ds-icons/ArrowRightIcon';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { PromptInputActions } from '#src/app/pages/homepage/shared/components';

type SubmitAreaProps = Readonly<{
	hasChip: boolean;
	isSubmitting: boolean;
	/** Rendered beside the button, or above it once the button fills the row. */
	message?: React.ReactNode;
}>;

/**
 * One button for both states so the swap animates from the arrow pill to the full-width label. The
 * label stays mounted, collapsed and aria-hidden, so the width transition has a stable child.
 */
export const SubmitArea: React.FC<SubmitAreaProps> = ({ hasChip, isSubmitting, message }) => {
	const { translate } = useI18n();
	const submitAriaLabel = isSubmitting
		? translate('corgi.homepage.hero_section.input_prompt_submit_button_loading')
		: translate('corgi.homepage.hero_section.input_prompt_submit_button');

	return (
		<PromptInputActions
			data-qa="prompt-input-actions"
			className={cn(
				'static mx-3 mt-3 mb-3',
				message ? 'justify-between' : 'justify-end',
				// no gap while the message is empty, so the button doesn't shift
				message && hasChip && 'flex-col items-stretch has-[output:empty]:gap-0',
			)}
		>
			{message}
			<Button
				aria-label={hasChip ? undefined : submitAriaLabel}
				aria-live="polite"
				// the spinner already takes the leading slot
				trailingIcon={isSubmitting ? undefined : <ArrowRightIcon />}
				loading={isSubmitting}
				variant="primary"
				type="submit"
				size="small"
				data-qa="prompt-input-actions-button"
				className={cn(
					// Fixed height keeps the collapsed pill a circle. `motion-safe:` and `!` let these win over
					// the button's own transition classes; background-color stays for its hover.
					'bg-fill-interactive motion-safe:duration-t4! h-12 shrink-0 rounded-full py-0 motion-safe:transition-[width,gap,padding,background-color] motion-safe:ease-[cubic-bezier(0.2,1.06,0.6,1)]!',
					hasChip ? 'w-full' : 'w-12 gap-0 px-0',
				)}
			>
				<span
					aria-hidden={!hasChip}
					className={cn(
						// max-width grows with the pill so the text never overflows it; only the fade is delayed
						'flex min-w-0 items-center overflow-hidden whitespace-nowrap',
						'motion-safe:duration-t4 motion-safe:transition-[max-width,opacity] motion-safe:ease-[cubic-bezier(0.2,1.06,0.6,1)]',
						hasChip ? 'max-w-[24rem] opacity-100 motion-safe:[transition-delay:0s,150ms]' : 'max-w-0 opacity-0',
					)}
				>
					<Translate value="corgi.homepage.hero_section.input_prompt_submit_button_generate" />
				</span>
			</Button>
		</PromptInputActions>
	);
};
